import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useClients, usePaperTypes, useTopFinishes, type LookupOption } from '../lib/lookups';
import { formatJc } from '../pages/Dashboard';

/**
 * Read-only detail popups for the records a queue row points at.
 *
 * Why a popup and not a link to the existing form page: a CRM is confined to
 * the dashboard and their own queue (CRM_ALLOWED_PATHS in App.tsx), so routing
 * them to /products/:id/edit bounced them straight back to /dashboard. They
 * could see the product name in their queue and had no way to read the
 * specification behind it. This shows the record in place, without navigating
 * away from a half-finished queue.
 *
 * Strictly read-only. Editing still lives on the form pages, behind
 * EDIT_PRODUCT / EDIT_PO, so this adds no capability to any role — it only
 * surfaces what VIEW_ALL already permits.
 */

export type PeekKind = 'product' | 'po' | 'job' | 'client';
export interface PeekTarget { kind: PeekKind; id: string }

const TITLES: Record<PeekKind, string> = {
  product: 'Product Master',
  po: 'Purchase Order',
  job: 'Job Card',
  client: 'Client Master',
};

export function RecordPeek({ target, onClose }: { target: PeekTarget; onClose: () => void }) {
  // Escape closes. Without this the only way out is the mouse, which is a poor
  // fit for a screen operators work through at speed.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:p-8"
      onClick={onClose}
role="presentation"
    >
      <div
        className="w-full max-w-3xl rounded bg-white shadow-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${TITLES[target.kind]} ${target.id}`}
      >
        <div className="card-header flex items-center justify-between">
          <span>{TITLES[target.kind]} — <span className="font-mono">{target.id}</span></span>
          <button type="button" onClick={onClose}
                  className="rounded px-2 text-lg leading-none hover:bg-black/10"
                  aria-label="Close">×</button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">
          <PeekBody target={target} />
        </div>
        <div className="flex justify-end border-t border-slate-100 p-3">
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
        </div>
    </div>
    </div>
  );
}

/**
 * Looks like a link, opens a popup.
 *
 * A <button> rather than an <a href>, because it does not navigate: screen
 * readers and middle-click would both be lied to by an anchor that goes
 * nowhere. `text-left` undoes the button default so it aligns with the
 * surrounding cell text.
 */
export function PeekLink({ children, onOpen, title, className = '' }: {
  children: React.ReactNode;
  onOpen: () => void;
  title: string;
  className?: string;
}) {
  return (
    <button type="button" onClick={onOpen} title={title}
            className={`text-left text-brand hover:underline ${className}`}>
      {children}
    </button>
  );
}

function PeekBody({ target }: { target: PeekTarget }) {
  // The print endpoint is used for job cards rather than the plain GET: it
  // already joins the PO, client, product and machine names, which is exactly
  // what makes the card readable rather than a list of ids.
  const url =
    target.kind === 'client'  ? `/api/clients/${encodeURIComponent(target.id)}`
    : target.kind === 'product' ? `/api/products/${encodeURIComponent(target.id)}`
    : target.kind === 'po'    ? `/api/purchase-orders/${encodeURIComponent(target.id)}`
    :                           `/api/job-orders/${encodeURIComponent(target.id)}/print`;

  const q = useQuery({
    queryKey: ['peek', target.kind, target.id],
    queryFn: () => api<Record<string, unknown>>(url),
    staleTime: 30_000,
  });

  if (q.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (q.isError) {
    const msg = (q.error as Error).message;
    return (
      <p className="text-sm text-red-700">
        {msg === 'not_found'
          // A CRM only sees products belonging to their own clients
          // (productScopeSql), so a 404 here is a scoping answer, not a bug.
          ? 'This record is not available to your account.'
          : `Could not load: ${msg}`}
      </p>
    );
  }
  const d = q.data ?? {};

  if (target.kind === 'product') return <ProductBody d={d} />;
  if (target.kind === 'po') return <PoBody d={d} />;
  if (target.kind === 'client') return <ClientBody d={d} />;
  return <JobBody d={d} />;
}

/* ----------------------------------------------------------------- bodies */

/**
 * Turn a master-data id into the name a human recognises.
 *
 * The product and PO DTOs return raw foreign keys — `PT038`, `TPFN_019`,
 * `Cli_185` — because the forms that normally consume them render a <select>
 * against the same lookup. A read-only popup has no select to fall back on, so
 * without this the panel shows codes nobody can read. Falls back to the id when
 * the lookup has not loaded or the code is a legacy value with no master row.
 */
function nameOf(list: LookupOption[] | undefined, id: unknown): string {
  const raw = s(id);
  if (!raw) return '';
  return list?.find((o) => o.id === raw)?.name ?? raw;
}

function ClientBody({ d }: { d: Record<string, unknown> }) {
  return (
    <>
      <Group title="Identity">
        <Field label="Name" value={s(d.name)} wide />
        <Field label="GST No." value={s(d.gstNo)} />
        <Field label="CIN" value={s(d.cin)} />
        <Field label="TIN" value={s(d.tin)} />
      </Group>
      <Group title="Contact">
        <Field label="Contact Person" value={s(d.contactPersonName)} />
        <Field label="Phone (Contact)" value={s(d.contactPersonPhone)} />
        <Field label="Company Phone" value={s(d.phone)} />
        <Field label="Email" value={s(d.email)} />
        <Field label="Billing Address" value={s(d.billingAddress)} wide />
      </Group>
    </>
  );
}

function ProductBody({ d }: { d: Record<string, unknown> }) {
  const papers = usePaperTypes();
  const finishes = useTopFinishes();
  const clients = useClients();
  const dims = [d.productLength, d.productWidth, d.productHeight].filter(Boolean).join(' × ');
  const cut = [d.sheetCutSizeLength, d.sheetCutSizeWidth].filter(Boolean).join(' × ');
  return (
    <>
      <Group title="Identity">
        <Field label="Product name" value={s(d.name)} wide />
        <Field label="Design No (artwork)" value={s(d.artwork)} />
        <Field label="A/W material no" value={s(d.artWNo)} />
        <Field label="Die no" value={s(d.dieNumber)} />
        <Field label="Plate no" value={s(d.plateNumber)} />
        <Field label="Past job orders" value={s(d.orderCount)} />
      </Group>
      <Group title="Board &amp; sheet">
        <Field label="Paper type" value={nameOf(papers.data, d.paperType)} />
        <Field label="Sheet size" value={s(d.sheetSize)} />
        <Field label="Cut size (mm)" value={cut} />
        <Field label="Party GSM" value={s(d.partyGsm)} />
        <Field label="Lamination" value={nameOf(finishes.data, d.laminationType)} />
        <Field label="Ups" value={s(d.noOfUps)} />
      </Group>
      <Group title="Product">
        <Field label="Client" value={nameOf(clients.data, d.client)} />
        <Field label="Dimensions (mm)" value={dims} />
        <Field label="Rate" value={s(d.productRate)} />
        <Field label="Carton type" value={s(d.cartonType)} />
      </Group>
      <Flags d={d} keys={[
        ['onlineEmbossing', 'Online embossing'], ['offlineEmbossing', 'Offline embossing'],
        ['braille', 'Braille'], ['spotUv', 'Spot UV'], ['screenUv', 'Screen UV'],
        ['onlineCoating', 'Online coating'], ['offlineCoating', 'Offline coating'],
        ['metallicCarton', 'Metallic carton'], ['threePlyCarton', '3-ply carton'],
      ]} />
      {s(d.remark) && (
        <Group title="Remark"><Field label="" value={s(d.remark)} wide /></Group>
      )}
    </>
  );
}

function PoBody({ d }: { d: Record<string, unknown> }) {
  const clients = useClients();
  return (
    <>
      <Group title="Order">
        <Field label="PO number" value={s(d.poNumber)} />
        <Field label="PO date" value={dt(d.poDate)} />
        <Field label="Requested delivery" value={dt(d.poDeliveryDate)} />
        <Field label="Order type" value={s(d.orderType)} />
        <Field label="Item code" value={s(d.materialNo)} />
        <Field label="A/W material no" value={s(d.awMaterialNo)} />
        <Field label="Design no" value={s(d.artwork)} />
      </Group>
      <Group title="Item">
        <Field label="Job name" value={s(d.jobName)} wide />
        <Field label="Product" value={s(d.productId)} />
        <Field label="Client" value={nameOf(clients.data, d.client)} />
      </Group>
      <Group title="Commercials">
        <Field label="Quantity" value={s(d.quantity)} />
        <Field label="Rate per box" value={s(d.pricePerBox)} />
        <Field label="Amount" value={s(d.amount)} />
        <Field label="VAT / GST %" value={s(d.vat)} />
        <Field label="Final amount" value={s(d.finalAmount)} />
      </Group>
      {s(d.description) && (
        <Group title="Description"><Field label="" value={s(d.description)} wide /></Group>
      )}
    </>
  );
}

/**
 * Job card. This one reads the raw print projection, so the keys are the
 * database column names rather than a camelCase DTO.
 */
function JobBody({ d }: { d: Record<string, unknown> }) {
  const cut = [d.Sheet_CutSize_length, d.Sheet_CutSize_Width].filter(Boolean).join(' × ');
  return (
    <>
      <Group title="Job">
        <Field label="JC #" value={formatJc(s(d.Job_ID))} />
        <Field label="Job order date" value={dt(d.JobOrder_date)} />
        <Field label="Status" value={s(d.Job_Staus)} />
        <Field label="Product" value={s(d.Product_Name ?? d.Job_Name)} wide />
        <Field label="Client" value={s(d.Client_Name)} />
      </Group>
      <Group title="Source PO">
        <Field label="PO number" value={s(d.PO_Number)} />
        <Field label="PO date" value={dt(d.PO_Date)} />
        <Field label="PO quantity" value={s(d.PO_Quantity)} />
        <Field label="Order type" value={s(d.PO_OrderType)} />
      </Group>
      <Group title="Quantities">
        <Field label="Total boxes" value={s(d.Total_Boxes)} />
        <Field label="Total sheet incl. wastage" value={s(d.TotalSheet_withWastage)} />
        <Field label="Ups" value={s(d.Ups)} />
        <Field label="Plate" value={s(d.Plate)} />
      </Group>
      <Group title="Specification">
        <Field label="Paper type" value={s(d.PaperType_Name ?? d.Paper_Type)} />
        <Field label="Sheet size" value={s(d.Resolved_Sheet_Size ?? d.Sheet_Size)} />
        <Field label="Cut size (mm)" value={cut} />
        <Field label="Lamination" value={s(d.Lamination_Type_Name ?? d.Lamination_Type)} />
        <Field label="Die no" value={s(d.Die_Cutting)} />
        <Field label="Selected die / artwork" value={s(d.Selected_Die)} />
        <Field label="Machine front" value={join(d.MachineFront_Name, d.MachineFront_Color)} />
        <Field label="Machine front 2" value={join(d.MachineFront2_Name, d.MachineFront2_Color)} />
        <Field label="Machine back" value={join(d.MachineBack_Name, d.MachineBack_Color)} />
      </Group>
      {s(d.Remarks) && (
        <Group title="Remarks"><Field label="" value={s(d.Remarks)} wide /></Group>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- pieces */

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-4 last:mb-0">
      <h3 className="mb-2 border-b border-slate-100 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
        {title}
      </h3>
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">{children}</dl>
    </section>
  );
}

function Field({ label, value, wide }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      {label && <dt className="text-[11px] uppercase tracking-wide text-slate-400">{label}</dt>}
      <dd className="text-sm text-slate-800 break-words">{value || '—'}</dd>
    </div>
  );
}

/** Boolean product options, shown only when at least one is set. */
function Flags({ d, keys }: { d: Record<string, unknown>; keys: [string, string][] }) {
  const on = keys.filter(([k]) => d[k]);
  if (on.length === 0) return null;
  return (
    <Group title="Finishing">
      <div className="sm:col-span-2 flex flex-wrap gap-1.5">
        {on.map(([k, label]) => (
          <span key={k} className="rounded border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs text-brand">
            {label}
          </span>
        ))}
      </div>
    </Group>
  );
}

function s(v: unknown): string {
  return v == null || v === '' ? '' : String(v);
}

function join(a: unknown, b: unknown): string {
  return [s(a), s(b)].filter(Boolean).join(' · ');
}

/** Dates arrive in mixed formats on this schema; show raw rather than guess. */
function dt(v: unknown): string {
  const raw = s(v);
  if (!raw) return '';
  const d = new Date(raw);
  return isNaN(d.getTime())
    ? raw
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
